import { useFlowNavigation } from '../hooks/useFlowNavigation';
import {
  useClearSceneDynamicFields,
  useDynamicType,
  useFictionalAnalysis,
  useRealConversation,
  useSetShowSettingsPopup,
  useShowSettingsPopup,
  useUndoRedo,
  useUpdatePlanningField,
} from '../store/hooks';
import { DYNAMIC_TYPE_OPTIONS, PlanningState } from '../types/planning';
import { countWords, isFieldCompleteByWords } from '../utils/dynamicBoxStyles';

import { SelectedRelationshipDisplay } from './SelectedRelationshipDisplay';
import { SettingsButton } from './SettingsButton';
import { SettingsPopup } from './SettingsPopup';
import { UnifiedBottomBar } from './UnifiedBottomBar';
import { UnifiedTopBar } from './UnifiedTopBar';

export default function SceneDynamic() {
  const { goToNextStep, goToPreviousStep } = useFlowNavigation();
  const dynamicType = useDynamicType();
  const realConversation = useRealConversation();
  const fictionalAnalysis = useFictionalAnalysis();
  const updatePlanningField = useUpdatePlanningField();
  const clearSceneDynamicFields = useClearSceneDynamicFields();
  const { undo, redo, canUndo, canRedo } = useUndoRedo();
  const showSettingsPopup = useShowSettingsPopup();
  const setShowSettingsPopup = useSetShowSettingsPopup();

  // Check completion status for each box
  const box1Complete = !!dynamicType;
  const box2Complete = isFieldCompleteByWords(realConversation, 50);
  const box3Complete = isFieldCompleteByWords(fictionalAnalysis, 50);

  const canContinue = box1Complete && box2Complete && box3Complete;

  // Determine which box is active based on completion
  const activeBoxIndex = !box1Complete ? 1 : !box2Complete ? 2 : !box3Complete ? 3 : 0;

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#050614] to-[#0f1a2f] flex flex-col">
      {/* Header */}
      <UnifiedTopBar
        subtitle="Scene Dynamic"
        stepInfo="Planner Stage 1 of 2"
        actions={<SettingsButton />}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-8 py-6">
          {/* Top Section: Title and Introduction */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <h2 className="text-3xl lg:text-4xl font-bold uppercase bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent">
                SCENE DYNAMIC
              </h2>

              {/* Selected Dynamic Display */}
              <SelectedRelationshipDisplay selectedValue={dynamicType} variant="prominent" />
            </div>
            <div className="max-w-4xl">
              <p className="text-gray-300 text-lg leading-relaxed text-balance">
                <strong>
                  Compelling interactive scenes aren&apos;t just about choice for the sake of
                  choice—they&apos;re rooted in the recognizable human dynamics and relationships we
                  see all around us. They&apos;re built on the <em>real</em> versions of these
                  choices we&apos;ve seen before or imagined in our own lives.
                </strong>
              </p>
            </div>
          </div>

          {/* Dynamic Type Selector Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Left: Dynamic Type Selector */}
            <div
              className={`rounded-xl p-6 transition-all duration-300 border ${
                box1Complete
                  ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#10b981]/30 shadow-lg shadow-[#10b981]/10'
                  : activeBoxIndex === 1
                    ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#4f46e5]/50 shadow-lg shadow-[#4f46e5]/20'
                    : 'bg-gradient-to-br from-[#0c1222]/50 to-[#0a1020]/50 border-[#1a2d4d] hover:border-[#1a2d4d]/70'
              }`}
            >
              <h3 className="text-base font-semibold mb-3 transition-colors duration-300">
                <span
                  className={`${
                    box1Complete
                      ? 'text-[#10b981]'
                      : activeBoxIndex === 1
                        ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent'
                        : 'text-gray-300'
                  }`}
                >
                  1. Choose your character&apos;s relationship
                </span>
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                What relationship do the two characters in your game have?
              </p>

              <div className="space-y-3">
                {DYNAMIC_TYPE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`block px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                      dynamicType === option.value
                        ? 'bg-gradient-to-r from-[#4f46e5]/10 to-[#06b6d4]/10 border-[#4f46e5]/50 shadow-lg shadow-[#4f46e5]/10'
                        : 'bg-[#0a0f1f] border-[#1a2d4d] hover:border-[#4f46e5]/30 hover:bg-[#0a0f1f]/80'
                    }`}
                  >
                    <input
                      type="radio"
                      name="dynamicType"
                      value={option.value}
                      checked={dynamicType === option.value}
                      onChange={(e) =>
                        updatePlanningField(
                          'dynamicType',
                          e.target.value as PlanningState['dynamicType']
                        )
                      }
                      className="sr-only"
                    />
                    <div className="flex items-start">
                      <div
                        className={`w-5 h-5 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                          dynamicType === option.value
                            ? 'border-[#4f46e5] bg-[#4f46e5]/10'
                            : 'border-gray-500 bg-[#0a0f1f]'
                        }`}
                      >
                        {dynamicType === option.value && (
                          <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#4f46e5] to-[#06b6d4]"></div>
                        )}
                      </div>
                      <div>
                        <div
                          className={`text-sm font-medium ${
                            dynamicType === option.value ? 'text-gray-200' : 'text-gray-300'
                          }`}
                        >
                          {option.label}
                        </div>
                        {option.subtitle && (
                          <div className="text-xs text-gray-500 mt-0.5">{option.subtitle}</div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Right: Helpful guidance boxes */}
            <div className="space-y-4">
              {/* Character Truth Box */}
              <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg">
                <h4 className="text-gray-300 font-semibold mb-3">The Power of Focus</h4>
                <p className="text-gray-300 text-sm leading-relaxed text-balance">
                  <strong>Character backstory distracts like real-life revelations do.</strong> Mary
                  the arsonist and William the cheater carry different dramatic weights than
                  &apos;Mary the mother&apos; or &apos;William the son&apos;. But reveal
                  they&apos;re mother and son? Place them at her husband&apos;s funeral, grieving
                  together? Everything else fades. People contain multitudes, but each moment
                  highlights one truth.{' '}
                  <strong>
                    Nothing matters more in writing than capturing that emotional truth.
                  </strong>
                </p>
              </div>

              {/* Analysis Tip Box */}
              <div className="bg-gradient-to-br from-[#0c1222]/50 to-[#0a1020]/50 border border-[#1a2d4d]/50 rounded-xl p-6">
                <h4 className="text-gray-300 font-semibold mb-3">Analysis Tip</h4>
                <p className="text-gray-400 text-sm leading-relaxed text-balance">
                  If you find it hard to analyse or answer some of these potential prompts -- then
                  don&apos;t forget: sometimes, ruling stuff out as -not being likely- can reveal a
                  great deal.
                </p>
              </div>
            </div>
          </div>

          {/* Horizontal Divider */}
          <div className="border-t border-[#1a2d4d] my-8"></div>

          {/* Bottom Section: Questions 2 and 3 side by side */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Question 2: Real Conversation - Left Column */}
            <div
              className={`rounded-xl p-6 flex flex-col h-[480px] transition-all duration-300 border ${
                box2Complete
                  ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#10b981]/30 shadow-lg shadow-[#10b981]/10'
                  : activeBoxIndex === 2
                    ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#4f46e5]/50 shadow-lg shadow-[#4f46e5]/20'
                    : 'bg-gradient-to-br from-[#0c1222]/50 to-[#0a1020]/50 border-[#1a2d4d] hover:border-[#1a2d4d]/70'
              }`}
            >
              <h3 className="text-base font-semibold mb-3 transition-colors duration-300">
                <span
                  className={`${
                    box2Complete
                      ? 'text-[#10b981]'
                      : activeBoxIndex === 2
                        ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent'
                        : 'text-gray-300'
                  }`}
                >
                  2. Recall a real conversation fitting the chosen dynamic
                </span>
              </h3>
              <div className="text-sm mb-4">
                <ul className="space-y-1.5 ml-2">
                  <li className="flex items-start">
                    <span className="text-[#4f46e5] mr-2">▸</span>
                    <span className="text-gray-400">
                      Why did this conversation (yours or observed) stick with you?
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#4f46e5] mr-2">▸</span>
                    <span className="text-gray-400">
                      What did people involved seem to want or try to achieve?
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#4f46e5] mr-2">▸</span>
                    <span className="text-gray-400">
                      What was the subtext: unspoken feelings, intentions, or power plays?
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#4f46e5] mr-2">▸</span>
                    <span className="text-gray-400">
                      Was there a &apos;pivot point&apos; altering its course or focus?
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#4f46e5] mr-2">▸</span>
                    <span className="text-gray-400">
                      How did the setting or any observers shape the interaction?
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex-1 flex flex-col">
                <textarea
                  value={realConversation}
                  onChange={(e) => updatePlanningField('realConversation', e.target.value)}
                  placeholder="Describe a real conversation that exemplifies this dynamic..."
                  className="w-full flex-1 bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-4 text-sm text-gray-200 placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] transition-all duration-200"
                />
                <div className="text-right text-xs mt-2">
                  <span className={box2Complete ? 'text-slate-400' : 'text-red-400'}>
                    Minimum: {countWords(realConversation)} / 50 words
                  </span>
                  {box2Complete && <span className="text-[#10b981] ml-2">✓</span>}
                </div>
              </div>
            </div>

            {/* Question 3: Fictional Analysis - Right Column */}
            <div
              className={`rounded-xl p-6 flex flex-col h-[480px] transition-all duration-300 border ${
                box3Complete
                  ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#10b981]/30 shadow-lg shadow-[#10b981]/10'
                  : activeBoxIndex === 3
                    ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#4f46e5]/50 shadow-lg shadow-[#4f46e5]/20'
                    : 'bg-gradient-to-br from-[#0c1222]/50 to-[#0a1020]/50 border-[#1a2d4d] hover:border-[#1a2d4d]/70'
              }`}
            >
              <h3 className="text-base font-semibold mb-3 transition-colors duration-300">
                <span
                  className={`${
                    box3Complete
                      ? 'text-[#10b981]'
                      : activeBoxIndex === 3
                        ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent'
                        : 'text-gray-300'
                  }`}
                >
                  3. Recall 2-3 examples of films, games, books &amp; other fictional works that
                  portray the chosen dynamic
                </span>
              </h3>
              <div className="text-sm mb-4 space-y-2">
                <p className="text-gray-400 font-medium">
                  Consider how these fictional examples achieve their impact:
                </p>
                <ul className="space-y-1.5 ml-2 mt-3">
                  <li className="flex items-start">
                    <span className="text-[#06b6d4] mr-2">◆</span>
                    <span className="text-gray-400">
                      Specific scene examples that feature a tense, revealing, or surprising
                      exchange
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#06b6d4] mr-2">◆</span>
                    <span className="text-gray-400">
                      What made them effective (stakes, subtext, unexpected moves, power shifts)?
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex-1 flex flex-col">
                <textarea
                  value={fictionalAnalysis}
                  onChange={(e) => updatePlanningField('fictionalAnalysis', e.target.value)}
                  placeholder="Analyze how this dynamic is represented in fiction - what works well and what doesn't..."
                  className="w-full flex-1 bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-4 text-sm text-gray-200 placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] transition-all duration-200"
                />
                <div className="text-right text-xs mt-1">
                  <span className={box3Complete ? 'text-slate-400' : 'text-red-400'}>
                    Minimum: {countWords(fictionalAnalysis)} / 50 words
                  </span>
                  {box3Complete && <span className="text-emerald-500 ml-2">✓</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Planner Navigation */}
      <UnifiedBottomBar
        variant="planner"
        onBack={goToPreviousStep}
        onContinue={goToNextStep}
        canContinue={canContinue}
        continueLabel="Continue to Scene Planning"
        onUndo={undo}
        onRedo={redo}
        onClear={clearSceneDynamicFields}
        canUndo={canUndo}
        canRedo={canRedo}
      />
      <SettingsPopup isOpen={showSettingsPopup} onClose={() => setShowSettingsPopup(false)} />
    </div>
  );
}
